import './Pricing.css'
import { type CSSProperties, useCallback, useEffect, useState } from 'react'

import type { UserProfile } from '@molecule/app-auth'
import { useAuth, useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex } from '@molecule/app-ui-react'

import { Header } from '../components/Header.js'
import { MoleculeSpinner } from '../components/MoleculeSpinner.js'
import { API_BASE_URL } from '../config.js'
import { useMediaQuery } from '../hooks/useMediaQuery.js'
import { emitNotification } from '../notifications.js'
import { reportHandledFailure } from '../utilities/error-reporting.js'
import { isGrantedSeatPlan } from '../utilities/usage-billing-utilities.js'
import { notifyUsageInvalidated } from '../utilities/usage-utilities.js'
import { useBillingPortal } from './Billing.js'

interface TierPrice {
  period: string
  price: string
  stripePriceId: string
  savings?: string
}

interface TierLimits {
  maxProjects: number
  sandboxTimeoutMinutes: number
  maxStorageMb: number
  /**
   * Days a DORMANT project stays ready before it is archived (api tiers.ts
   * `TierLimits.dormantRetentionDays`). OPTIONAL because an API predating the
   * field sends no value — the row is then omitted rather than rendering a
   * made-up "0 days".
   */
  dormantRetentionDays?: number
  maxActiveDeployments: number
  /**
   * How many people can BUILD on one project, INCLUDING the owner (api tiers.ts
   * `TierLimits.maxBuilders`). Viewers are unlimited on every plan and are not
   * counted here, so `1` means "you, plus as many onlookers as you like".
   */
  maxBuilders: number
  maxCustomDomains: number
  prioritySupport: string
  /**
   * The tier's Synthase build allowance and the window it covers (api tiers.ts
   * `TierLimits.aiBudget`). Neither field is rendered — `cents` is deliberately
   * never shown (see {@link AI_SUMMARY}). Declared because the field is really
   * in the payload, and OPTIONAL because an API predating it sends no value.
   */
  aiBudget?: { cents: number; window: 'daily' | 'billing-period' }
}

export interface TierInfo {
  key: string
  name: string
  prices?: TierPrice[]
  price?: string | null
  perSeat?: boolean
  limits: TierLimits
  /**
   * Selectable AI-allowance steps for this tier, per billing period (api
   * tiers.ts `usagePackOptions`). A step is the SAME plan with a bigger
   * allowance and its own price, so picking one just changes which plan key
   * checkout is started for. Absent (or a single `1×` entry) means the operator
   * has configured no packs — the selector is then not rendered at all.
   */
  usageOptions?: Partial<Record<'month' | 'year', UsageOption[]>>
  /**
   * The tier's usage rate card (api tiers.ts `TierBilling`): the published
   * cost-plus markup on metered AI, the default spend cap, and the hosted-AI
   * allowance for a user's DEPLOYED apps. Sent by `GET /tiers`. Not rendered
   * on the cards (most users never touch brokered usage, and the AI-usage
   * figure a signing-up user is choosing between is the allowance step).
   * Declared because the field is really in the payload: leaving it off the
   * type made the page's own fixture unassignable to it, which is the type
   * lying about the API.
   */
  billing?: {
    includedBrokerCents: number
    overageMarkup: number
    defaultSpendCapCents: number
  }
}

/** One selectable allowance step (api tiers.ts `usagePackOptions`). */
export interface UsageOption {
  /** The pack multiple applied on top of the tier's own allowance; 1 = the plan as sold. */
  multiple: number
  /**
   * What the step is CALLED (`Standard` / `Extended` / `Max`). Never rendered as
   * a multiple: the plan's own allowance is sold below cost and a step's is
   * priced cost-plus, so "2×" invites dividing $79 by $19 and reading a
   * rip-off out of two numbers that share no basis (api tiers.ts
   * `USAGE_PACK_NAMES`).
   */
  name: string
  /** The plan key to check out for this step. */
  planKey: string
  /** The Stripe price backing it; a step with none is never offered by the API. */
  stripePriceId: string | null
  /** This step's own display price (`$79/mo`) — NOT the base plan's. */
  price: string
  /** How far below the metered rate this step lands; `null` on the 1x step. */
  savings: string | null
}

const DASH = '\u2014'

/**
 * The total for N seats, from a per-seat price string like `$49/mo/seat`.
 *
 * Reads the amount out of the display string rather than taking a number,
 * because a price is only ever sent as formatted text \u2014 and the alternative,
 * showing a per-seat figure next to a seat count and leaving the buyer to do the
 * arithmetic, is how someone arrives at checkout expecting a different number.
 *
 * Returns `''` when the string carries no parseable amount, so a caller renders
 * nothing rather than `$NaN`.
 *
 * @param price - The per-seat display price.
 * @param seats - How many seats are selected.
 * @returns A total like `$147/mo total`, or `''`.
 */
export function seatTotal(price: string, seats: number): string {
  const amount = Number((price.match(/[\d,]+(\.\d+)?/) || [''])[0].replace(/,/g, ''))
  if (!Number.isFinite(amount) || amount <= 0) return ''
  const period = price.includes('/yr') ? '/yr' : '/mo'
  return `$${(amount * seats).toLocaleString('en-US')}${period} total`
}

/**
 * What each plan's Synthase allowance BUYS, in the buyer's terms.
 *
 * A multiple ("5×") only means anything next to a baseline, and the free tier
 * is not one a buyer should be told to measure in — so each tier says what it
 * gets you instead. Kept to one short line: the number behind it (a per-tier
 * upstream-spend budget) is deliberately never shown, since AI usage is
 * unitless on every product surface and cents would publish our cost basis.
 *
 * The escalation is the pitch: try one thing → ship your thing → keep a whole
 * team shipping. Team's line is about the team as a unit, not a seat count —
 * headcount is a row below, and building here is a shared activity.
 */
const AI_SUMMARY: Record<string, string> = {
  free: 'Try a few small ideas',
  pro: 'Build and ship a full app',
  team: 'Keep your whole team building and shipping',
}

/**
 * Per-tier visual identity, driven by ONE scoped variable.
 *
 * A paid card re-points `--color-primary` (and its derived text mix) to the
 * tier's accent for its own subtree, so everything token-driven inside the card
 * — the border, the tint, the CTA button (the button classes read
 * `var(--color-primary)` directly), and the −/+ hover washes — comes out in the
 * same color without styling each one. Pro keeps the global blue (no override —
 * a self-referential `--color-primary: var(--color-primary)` is a CSS cycle that
 * blanks the value), Team is `info` (teal, deliberately not `success` green,
 * which the savings line already owns). Free has no accent: a neutral border and
 * no purchasable CTA.
 *
 * `override` is the scoped custom-property block a card spreads into its style
 * (empty for Pro). The border and tint always reference `var(--color-primary)`,
 * so they follow whatever the card scoped it to.
 */
const TIER_ACCENT: Record<string, { override: Record<string, string> }> = {
  pro: { override: {} },
  team: {
    override: {
      '--color-primary': 'var(--color-info)',
      // Mirror app index.css's own primary-text mix (75% toward foreground) so
      // the button label is the teal-derived text, not the inherited blue one.
      '--color-primary-text': 'color-mix(in srgb, var(--color-info) 75%, var(--color-foreground))',
    },
  },
}

/**
 * Minimum seats a Team subscription is sold at. A one-person "team" gets none
 * of Team's actual differentiator (more than one builder on a project) — it is
 * just a pricier Pro — so the seat stepper floors here rather than at 1, and
 * Team always bills at least this many seats. Kept in lockstep with the
 * authoritative server floor (`api tiers.ts` `MIN_TEAM_SEATS` /
 * `seatQuantityForCheckout`), which the api/app boundary prevents importing.
 */
const MIN_TEAM_SEATS = 2

/**
 * Headcount in the buyer's words: `1 person` / `2 people`. Team never renders 1
 * (its floor is {@link MIN_TEAM_SEATS}), but the singular is kept correct so the
 * helper reads right anywhere.
 *
 * @param count - Selected seats.
 * @returns A pluralized headcount label.
 */
function seatCountLabel(count: number): string {
  return count === 1 ? '1 person' : `${count} people`
}

/**
 * Display labels for the server's generic `prioritySupport` levels (the
 * support channel is deliberately undecided; see api tiers.ts).
 */
const SUPPORT_LABELS: Record<string, string> = {
  priority: 'Priority',
  highest: 'High priority',
  dedicated: 'Dedicated',
}

function formatLimit(val: number): string {
  if (val >= 999999) return 'Unlimited'
  return String(val)
}

/**
 * The base plan key a tier checks out for a billing period (a usage pack
 * carries its own `planKey` instead). Mirrors the api tiers.ts catalogue keys;
 * returns `''` for tiers with no purchasable plan (free / unknown).
 */
function basePlanKey(tierKey: string, period: 'month' | 'year'): string {
  if (tierKey === 'pro') return period === 'year' ? 'stripeYearly' : 'stripeMonthly'
  if (tierKey === 'team') return period === 'year' ? 'teamYearly' : 'teamMonthly'
  return ''
}

/**
 * Height of the usage-stepper row, reserved on cards that don't render one so
 * every card's limits table starts on the same line. Sized to the stepper's own
 * metrics: a 16px/1.2 glyph + 2px vertical padding + the pill's 1px borders.
 */
const USAGE_ROW_HEIGHT = 25
/**
 * The same reserved row on coarse-pointer (touch) devices, where the −/+
 * buttons grow to the 36px compact touch floor (cm.touchTargetCompact): 36px
 * buttons + the pill's 1px borders. Every card reserves the same value per modality, so the
 * shared-baseline alignment of the limits tables holds on tablets too.
 */
const USAGE_ROW_HEIGHT_COARSE = 38

/**
 * Style for the −/+ buttons of a stepper. Background is intentionally NOT set
 * here — the `.mol-price-step-btn` class owns it so the `:hover` wash isn't
 * overridden by an inline value.
 */
function usageStepButtonStyle(disabled: boolean): CSSProperties {
  return {
    padding: '2px 10px',
    border: 'none',
    color: 'inherit',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.35 : 1,
    fontSize: 16,
    lineHeight: 1.2,
  }
}

/**
 * The two billing periods, in the order they sit in the toggle. The toggle
 * derives its geometry from this (each segment is `100 / length` % wide), so the
 * thumb lands correctly without any per-option arithmetic at the call site.
 */
const BILLING_PERIODS = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
] as const

/**
 * Label style for one position of the Monthly/Yearly billing toggle.
 *
 * The labels are TRANSPARENT and non-interactive: the selected state is drawn by
 * the thumb sliding behind them, and the click target is the whole control.
 * Filling the active half made this read as two separate buttons that happened
 * to highlight — the moving thumb is what makes it read as one toggle.
 */
function billingToggleSegmentStyle(active: boolean): CSSProperties {
  return {
    position: 'relative',
    zIndex: 1,
    flex: 1,
    padding: '5px 18px',
    borderRadius: 999,
    background: 'transparent',
    // White on the solid-primary thumb (same on-primary treatment as the
    // UsageNudge buttons) — black on the blue was hard to read in light theme.
    color: active ? '#fff' : 'inherit',
    // Opacity is NOT set here — `.mol-price-toggle-label` owns it (base + the
    // active `[data-active]` step + the toggle's `:hover` lift), which an inline
    // value would override.
    fontWeight: 600,
    fontSize: 13,
    whiteSpace: 'nowrap',
    transition: 'color 0.2s ease',
  }
}

function TierCard({
  tier,
  billingPeriod,
  currentPlan,
  onUpgrade,
  loading,
  upgradeHref,
  onCancelPlan,
  cancelPending,
  reserveUsageRow,
  reserveSeatTotal,
}: {
  tier: TierInfo
  billingPeriod: 'month' | 'year'
  currentPlan: string | null
  onUpgrade: (priceId: string, planKey?: string, quantity?: number) => void
  loading: boolean
  /**
   * Whether ANY tier shows a usage stepper for this billing period — decided by
   * the parent, which is the only place that sees all three cards. True holds
   * the row's height open here so a stepper on the paid cards doesn't push their
   * limits tables below Free's.
   */
  reserveUsageRow?: boolean
  /**
   * Whether ANY tier is per-seat (and so shows a seats-total line). Same
   * shared-baseline reason as {@link reserveUsageRow}: the non-Team cards hold
   * the total row's height open so Team's total can't drop its table below
   * theirs.
   */
  reserveSeatTotal?: boolean
  /**
   * When set (in-IDE modal + signed out), the paid-tier CTA renders as a link
   * to this path instead of the checkout button — the workspace's CTA
   * interceptor then opens the auth modal in place.
   */
  upgradeHref?: string
  /**
   * Free card only, and only for a SUBSCRIBER: the way out of a paid plan. When
   * set, Free's button becomes a live "Cancel plan" CTA instead of the inert
   * one — a subscriber who opens pricing to leave should not have to know that
   * the exit lives in a settings panel. Absent for a free user (nothing to
   * cancel) and while signed out.
   */
  onCancelPlan?: () => void
  /** Whether the cancel CTA's own request is in flight (page variant only). */
  cancelPending?: boolean
}) {
  const cm = getClassMap()
  const { t } = useTranslation()
  // Touch devices grow the stepper buttons to the 36px compact floor, so the reserved
  // row height must follow (see USAGE_ROW_HEIGHT_COARSE).
  const coarsePointer = useMediaQuery('(pointer: coarse)')
  const usageRowHeight = coarsePointer ? USAGE_ROW_HEIGHT_COARSE : USAGE_ROW_HEIGHT
  const price = tier.prices?.find((p) => p.period === billingPeriod)

  // Whether the user is on SOME variant of this tier (any period / usage pack).
  const onThisTier = Boolean(
    currentPlan &&
    (currentPlan === tier.key ||
      (tier.key === 'pro' && currentPlan.startsWith('stripe')) ||
      (tier.key === 'team' && currentPlan.startsWith('team'))),
  )

  // Each tier's accent paints its border + CTA the same color (see
  // {@link TIER_ACCENT}); Free has none.
  const accent = TIER_ACCENT[tier.key]

  // Selectable allowance steps for the CURRENT billing period. Each is a plan
  // key of its own, so choosing one only changes what checkout is started for.
  // A tier with no configured packs has at most the implicit 1× entry — then
  // there is nothing to choose and the selector is not rendered.
  const usageOptions = tier.usageOptions?.[billingPeriod] ?? []
  // Default the stepper to the user's CURRENT pack when they're on one, so the
  // first paint reads "Current Plan" rather than a switch CTA for a selection
  // they never made.
  const [usageMultiple, setUsageMultiple] = useState(
    () => usageOptions.find((option) => option.planKey === currentPlan)?.multiple ?? 1,
  )
  const selectedUsage =
    usageOptions.find((option) => option.multiple === usageMultiple) ?? usageOptions[0] ?? null

  // Seats, on a per-seat tier. A seat buys a PERSON — their own AI allowance,
  // metered to them — so the card multiplies the price by the count but never
  // implies the buyer receives that much AI themselves. Team is sold at
  // MIN_TEAM_SEATS or more (a one-person team is just a pricier Pro), so the
  // stepper opens and floors there rather than at 1.
  const minSeats = tier.perSeat ? MIN_TEAM_SEATS : 1
  const [seats, setSeats] = useState(minSeats)
  const seatCount = tier.perSeat
    ? Math.min(Math.max(seats, minSeats), tier.limits.maxBuilders || minSeats)
    : 1

  // Position of the selected step within the offered ones, so the −/+ controls
  // can move by STEP rather than by 1 (the steps are discrete: each needs its
  // own price) and disable themselves at either end.
  const selectedIndex = Math.max(
    0,
    usageOptions.findIndex((option) => option.multiple === (selectedUsage?.multiple ?? 1)),
  )
  const stepTo = (index: number): void => {
    const next = usageOptions[index]
    if (next) setUsageMultiple(next.multiple)
  }

  // The exact plan key the card's CURRENT selection (period + pack) checks out
  // for. "Current Plan" disables the CTA only on an exact match — a
  // stripeMonthly user viewing Yearly gets a switch CTA, not a dead card. The
  // bare tier key has no variants, so it always counts as exact.
  const cardPlanKey = selectedUsage?.planKey ?? basePlanKey(tier.key, billingPeriod)
  const isCurrentPlan =
    (tier.key === 'free' && !currentPlan) ||
    (onThisTier && (currentPlan === cardPlanKey || currentPlan === tier.key))

  // CTA label: cross-tier moves say "Upgrade"; within the tier, name the change
  // — the other billing period, or (pack change / pack + period) a plan change.
  const otherPeriodKey =
    billingPeriod === 'year'
      ? cardPlanKey.replace('Yearly', 'Monthly')
      : cardPlanKey.replace('Monthly', 'Yearly')
  const ctaLabel = !onThisTier
    ? 'Upgrade'
    : currentPlan === otherPeriodKey
      ? billingPeriod === 'year'
        ? 'Switch to Yearly'
        : 'Switch to Monthly'
      : 'Change Plan'

  const aiSummary = AI_SUMMARY[tier.key]

  // The per-unit price shown top-right. A selected pack carries its own price;
  // otherwise it is the base period price. On a per-seat tier it always reads
  // "…/seat" — the pack price from the API drops that suffix, and a Team unit
  // price without it reads as the whole-team total.
  const rawUnitPrice =
    selectedUsage && selectedUsage.multiple > 1
      ? selectedUsage.price
      : (price?.price ?? tier.prices?.[0]?.price ?? '')
  const unitPrice =
    tier.perSeat && rawUnitPrice && !rawUnitPrice.includes('/seat')
      ? `${rawUnitPrice}/seat`
      : rawUnitPrice

  // What the buyer actually pays, once seats multiply the per-seat rate. Shown
  // only on a per-seat tier (Team always has >1 seat), so a single number is
  // never labeled "total" next to itself.
  const totalText = tier.perSeat && seatCount > 1 ? seatTotal(rawUnitPrice, seatCount) : ''

  // Sandbox idle timeout, rendered from `limits.sandboxTimeoutMinutes`. 999999 is
  // the API's never-hibernate sentinel (hibernation.ts); an hour or more reads in
  // hours ("2 h", "1.5 h") because "120 min" next to "5 min" / "30 min" hides how
  // much larger the window is. One decimal at most — tiers are whole or half hours.
  const timeoutMinutes = tier.limits.sandboxTimeoutMinutes
  const timeoutHours = Number((timeoutMinutes / 60).toFixed(1))
  const timeoutValue =
    timeoutMinutes >= 999999
      ? t('pricing.limits.timeoutAlwaysOn', undefined, { defaultValue: 'Always-on' })
      : timeoutMinutes >= 60
        ? t(
            'pricing.limits.timeoutHours',
            { hours: timeoutHours },
            { defaultValue: `${timeoutHours} h` },
          )
        : t(
            'pricing.limits.timeoutMinutes',
            { minutes: timeoutMinutes },
            { defaultValue: `${timeoutMinutes} min` },
          )

  // Dormant-project retention, rendered from the API's tier data
  // (`limits.dormantRetentionDays`) exactly like the sandbox-timeout and
  // storage rows below — a change in api `tiers.ts` reprices this line, nothing
  // is hardcoded. `null` (older API sending no value) omits the row entirely.
  const retentionDays = tier.limits.dormantRetentionDays
  const retentionValue =
    typeof retentionDays !== 'number' || !Number.isFinite(retentionDays)
      ? null
      : retentionDays >= 999999
        ? t('pricing.limits.retentionAlways', undefined, { defaultValue: 'Always' })
        : retentionDays === 1
          ? t('pricing.limits.retentionDay', { days: retentionDays }, { defaultValue: '1 day' })
          : t(
              'pricing.limits.retentionDays',
              { days: retentionDays },
              { defaultValue: `${retentionDays} days` },
            )

  return (
    <div
      style={{
        // A paid card scopes `--color-primary` to its accent (see TIER_ACCENT),
        // so the border, tint, CTA button and −/+ hovers below all resolve to
        // the same color from one variable. Free has no accent — a neutral
        // border and no purchasable CTA.
        ...accent?.override,
        border: accent
          ? '2px solid var(--color-primary)'
          : '1px solid var(--color-border-secondary)',
        borderRadius: 12,
        // 16px horizontal (vs 20) buys the Team tagline the width to stay on one
        // line at 3-across without shrinking it below reading size.
        padding: '20px 16px',
        flex: '1 1 280px',
        maxWidth: 360,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        background: accent
          ? 'color-mix(in srgb, var(--color-primary) 5%, transparent)'
          : 'transparent',
      }}
    >
      {/* Header, as stacked rows so the two columns line up EXACTLY: the people
          −/+ and AI −/+ share one line at the TOP, then name↔price share one
          baseline, then the per-seat total sits directly beneath the price.
          Every reserved row renders on all three cards (empty where a tier lacks
          it) so a control one tier has can never shift another tier's rows. */}

      {/* Controls row — people −/+ (left) and AI −/+ (right) on ONE line, at the
          top of the card. Reserved at the stepper's height on every card when
          any card has a stepper, so an empty cell holds its place and the AI
          stepper stays hard right. */}
      {(reserveUsageRow || reserveSeatTotal) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            minHeight: usageRowHeight,
            marginBottom: 8,
          }}
          data-mol-id={`pricing-controls-${tier.key}`}
        >
          {/* People −/+ — only on a per-seat tier that holds more than one
              person; Team floors at MIN_TEAM_SEATS. The empty span keeps the AI
              stepper right-aligned when there is no people stepper. */}
          {tier.perSeat && (tier.limits.maxBuilders ?? minSeats) > 1 ? (
            <div
              className="mol-price-stepper"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: 999,
                overflow: 'hidden',
                height: usageRowHeight,
              }}
              data-mol-id={`pricing-seats-picker-${tier.key}`}
            >
              <button
                type="button"
                onClick={() => setSeats((n) => Math.max(minSeats, n - 1))}
                disabled={seatCount <= minSeats}
                aria-label={t('pricing.seats.fewer', undefined, { defaultValue: 'Fewer people' })}
                data-mol-id={`pricing-seats-fewer-${tier.key}`}
                className={cm.cn(cm.touchTargetCompact, 'mol-price-step-btn')}
                style={usageStepButtonStyle(seatCount <= minSeats)}
              >
                −
              </button>
              <span
                style={{ minWidth: 68, textAlign: 'center', fontSize: 13, fontWeight: 700 }}
                data-mol-id={`pricing-seats-value-${tier.key}`}
              >
                {t(
                  'pricing.seats.count',
                  { count: seatCount },
                  { defaultValue: seatCountLabel(seatCount) },
                )}
              </span>
              <button
                type="button"
                onClick={() =>
                  setSeats((n) => Math.min(tier.limits.maxBuilders || minSeats, n + 1))
                }
                disabled={seatCount >= (tier.limits.maxBuilders || minSeats)}
                aria-label={t('pricing.seats.more', undefined, { defaultValue: 'More people' })}
                data-mol-id={`pricing-seats-more-${tier.key}`}
                className={cm.cn(cm.touchTargetCompact, 'mol-price-step-btn')}
                style={usageStepButtonStyle(seatCount >= (tier.limits.maxBuilders || minSeats))}
              >
                +
              </button>
            </div>
          ) : (
            <span aria-hidden="true" />
          )}

          {/* AI −/+ — only where the operator configured extra steps. */}
          {usageOptions.length > 1 ? (
            <div
              className="mol-price-stepper"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: 999,
                overflow: 'hidden',
                height: usageRowHeight,
              }}
              data-mol-id={`pricing-usage-picker-${tier.key}`}
            >
              <button
                type="button"
                onClick={() => stepTo(selectedIndex - 1)}
                disabled={selectedIndex <= 0}
                aria-label={t('pricing.usagePack.less', undefined, {
                  defaultValue: 'Less AI usage',
                })}
                data-mol-id={`pricing-usage-less-${tier.key}`}
                className={cm.cn(cm.touchTargetCompact, 'mol-price-step-btn')}
                style={usageStepButtonStyle(selectedIndex <= 0)}
              >
                −
              </button>
              <span
                style={{ minWidth: 84, textAlign: 'center', fontSize: 13, fontWeight: 700 }}
                data-mol-id={`pricing-usage-value-${tier.key}`}
              >
                {/* "Standard AI" / "Extended AI" / "Max AI" — keyed by multiple so
                    a locale can translate the size name. */}
                {t(`pricing.usagePack.name.${selectedUsage?.multiple ?? 1}`, undefined, {
                  defaultValue: selectedUsage?.name ?? 'Standard',
                })}{' '}
                AI
              </span>
              <button
                type="button"
                onClick={() => stepTo(selectedIndex + 1)}
                disabled={selectedIndex >= usageOptions.length - 1}
                aria-label={t('pricing.usagePack.more', undefined, {
                  defaultValue: 'More AI usage',
                })}
                data-mol-id={`pricing-usage-more-${tier.key}`}
                className={cm.cn(cm.touchTargetCompact, 'mol-price-step-btn')}
                style={usageStepButtonStyle(selectedIndex >= usageOptions.length - 1)}
              >
                +
              </button>
            </div>
          ) : (
            <span aria-hidden="true" />
          )}
        </div>
      )}

      {/* Name ↔ price, on one baseline. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <h3 className={cm.cn(cm.textSize('3xl'), cm.fontWeight('bold'))}>{tier.name}</h3>
        <div
          style={{ fontSize: 20, fontWeight: 700, whiteSpace: 'nowrap' }}
          data-mol-id={`pricing-price-${tier.key}`}
        >
          {tier.key !== 'free' ? unitPrice : ''}
        </div>
      </div>

      {/* Total row — what a Team actually pays, directly beneath the per-seat
          rate. Reserved on every card when any tier is per-seat, so the rows
          below start at the same height on all three; only the populated one has
          the id. */}
      {reserveSeatTotal && (
        <div
          style={{
            textAlign: 'right',
            // Larger than a caption, but a step below the per-seat price above
            // (fontSize 20) so the unit rate stays the headline number.
            fontSize: 17,
            fontWeight: 600,
            opacity: 0.75,
            minHeight: 22,
            marginTop: 6,
          }}
          data-mol-id={totalText ? `pricing-seats-total-${tier.key}` : undefined}
        >
          {totalText}
        </div>
      )}

      {/* What this plan's AI allowance BUYS — see {@link AI_SUMMARY} for why no
          number appears here. One reading-size line; the fixed `minHeight` on
          every card keeps the limits tables on a shared baseline even if a
          translation is longer than the English. Team's sentence — the longest —
          now fits one line at this size and the card's width (see the card
          padding above). */}
      {aiSummary && (
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            lineHeight: 1.35,
            letterSpacing: '-0.2px',
            marginTop: 12,
            minHeight: '1.5em',
          }}
          data-mol-id={`pricing-ai-summary-${tier.key}`}
        >
          {t(`pricing.aiSummary.${tier.key}`, undefined, { defaultValue: aiSummary })}
        </div>
      )}

      <div
        style={{ borderTop: '1px solid var(--color-border)', margin: '12px 0', paddingTop: 12 }}
        data-mol-id={`pricing-limits-${tier.key}`}
      >
        <FeatureRow label="Projects" value={formatLimit(tier.limits.maxProjects)} />
        <FeatureRow label="Sandbox timeout" value={timeoutValue} />
        {retentionValue !== null && (
          <FeatureRow
            label={t('pricing.limits.retention', undefined, {
              defaultValue: 'Stays warm',
            })}
            value={retentionValue}
            molId={`pricing-retention-${tier.key}`}
          />
        )}
        <FeatureRow
          label="Storage"
          value={
            tier.limits.maxStorageMb >= 50000
              ? `${tier.limits.maxStorageMb / 1024}GB`
              : tier.limits.maxStorageMb >= 1024
                ? `${tier.limits.maxStorageMb / 1024}GB`
                : `${tier.limits.maxStorageMb}MB`
          }
        />
        {/* "Live apps", not "Active deployments": the cap is how many of your
            projects can be running in production at once (one live container per
            project), which is what a buyer is choosing between — not how many
            times you may press Deploy. */}
        <FeatureRow
          label={t('pricing.limits.liveApps', undefined, { defaultValue: 'Live apps' })}
          value={formatLimit(tier.limits.maxActiveDeployments)}
          molId={`pricing-live-apps-${tier.key}`}
        />
        <FeatureRow
          label="Custom domains"
          value={
            tier.limits.maxCustomDomains === 0 ? DASH : formatLimit(tier.limits.maxCustomDomains)
          }
        />
        <FeatureRow label="Support" value={SUPPORT_LABELS[tier.limits.prioritySupport] ?? DASH} />
        {/* No "Build together" row: building with more than one person is what
            picking Team already means, and the people −/+ in the header shows the
            headcount directly. A limits row for it just restated the tier. */}
      </div>

      {/* cm.touchTargetCompact: 36px-floor CTAs only on coarse pointers; desktop unchanged.
          Free has nothing to buy, so its button is inert — "Current" on the free
          plan, otherwise "Free". The ONE exception is a subscriber: for them the
          Free card IS the downgrade, so it carries a live "Cancel plan" CTA
          ({@link onCancelPlan}) rather than making them find the exit in
          settings. Paid CTAs need no color prop: the button classes read
          `var(--color-primary)`, which the card scoped to its accent, so each
          card's button matches its border automatically. */}
      <div style={{ marginTop: 'auto' }}>
        {tier.key === 'free' ? (
          // Gray (secondary), to match the Free card's neutral border rather than
          // wear the blue primary the other cards' buttons use. Outline when it
          // is live: leaving a plan is a utility action, never a CTA that
          // competes with the two cards selling one (molecule/DESIGN.md → CTA
          // color semantics).
          onCancelPlan ? (
            <Button
              variant="outline"
              color="secondary"
              className={cm.touchTargetCompact}
              style={{ width: '100%' }}
              onClick={onCancelPlan}
              disabled={cancelPending}
              data-mol-id="pricing-cta-free"
            >
              {cancelPending ? (
                <MoleculeSpinner size="sm" />
              ) : (
                t('pricing.cta.cancel', undefined, { defaultValue: 'Cancel plan' })
              )}
            </Button>
          ) : (
            <Button
              disabled
              color="secondary"
              className={cm.touchTargetCompact}
              style={{ width: '100%', opacity: 0.6 }}
              data-mol-id="pricing-cta-free"
            >
              {isCurrentPlan
                ? t('pricing.cta.current', undefined, { defaultValue: 'Current' })
                : t('pricing.cta.free', undefined, { defaultValue: 'Free' })}
            </Button>
          )
        ) : isCurrentPlan ? (
          // The "you're on this plan" state — now the SAME color as the Free
          // card's own current-plan button above (disabled + secondary). It used
          // to fall through to the default PRIMARY, so one state wore the
          // selling color on paid cards and the neutral one on Free.
          // (The label still differs — "Current Plan" here vs "Current" on the
          // Free card — but that is product copy, not styling; left as-is.)
          <Button
            disabled
            color="secondary"
            className={cm.touchTargetCompact}
            style={{ width: '100%', opacity: 0.6 }}
            data-mol-id={`pricing-current-${tier.key}`}
          >
            {t('pricing.cta.currentPlan', undefined, { defaultValue: 'Current Plan' })}
          </Button>
        ) : upgradeHref ? (
          // Upgrade → primary, on every surface that sells a plan
          // (molecule/DESIGN.md → "CTA color semantics").
          <a
            href={upgradeHref}
            className={cm.cn(
              cm.button({ variant: 'solid', color: 'primary', size: 'md' }),
              cm.touchTargetCompact,
            )}
            style={{ width: '100%', textDecoration: 'none' }}
            data-mol-id={`pricing-upgrade-${tier.key}`}
          >
            {t('pricing.cta.upgrade', undefined, { defaultValue: 'Upgrade' })}
          </a>
        ) : (
          <Button
            className={cm.touchTargetCompact}
            style={{ width: '100%' }}
            // The button that actually starts checkout, and the only interactive
            // element on this card that had no id — so the one thing an
            // end-to-end test most needs to click was the one it could not find.
            data-mol-id={`pricing-checkout-${tier.key}`}
            onClick={() => {
              // A selected pack carries its own plan key + price; with no packs
              // configured this is exactly the previous behaviour.
              const priceId = selectedUsage?.stripePriceId ?? price?.stripePriceId
              if (priceId) {
                // Seats are what the buyer chose; the provider bills that many.
                onUpgrade(priceId, cardPlanKey || undefined, seatCount)
              } else {
                // No Stripe price behind this option (operator config gap, e.g.
                // an unset STRIPE_*_PRICE_ID). Say so instead of dead-clicking,
                // and leave a queryable trace so it can be alerted on.
                reportHandledFailure(
                  'plan-upgrade',
                  new Error(`No Stripe price configured for tier "${tier.key}" (${billingPeriod})`),
                )
                emitNotification('This plan cannot be purchased right now.', 'error')
              }
            }}
            disabled={loading}
          >
            {loading ? <MoleculeSpinner size="sm" /> : ctaLabel}
          </Button>
        )}
      </div>
    </div>
  )
}

function FeatureRow({
  label,
  value,
  molId,
}: {
  label: string
  value: string
  /** Optional `data-mol-id` so a specific limit row can be targeted directly. */
  molId?: string
}) {
  return (
    <div
      style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 14 }}
      data-mol-id={molId}
    >
      <span style={{ opacity: 0.7 }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  )
}

/** Props for {@link PricingContent}. */
export interface PricingContentProps {
  /**
   * Where the content renders: the standalone `/pricing` page (default), or the
   * in-IDE upgrade modal. In the modal, Stripe checkout opens in a new tab (an
   * external payment page can't render in-app and the IDE tab must never
   * navigate) and a signed-out user's CTA routes through the auth modal.
   */
  variant?: 'page' | 'modal'
  /** Modal only: called right after a Stripe checkout tab is opened. */
  onCheckoutOpened?: () => void
  /**
   * Modal only: switch the modal to its billing view, where "Manage billing"
   * opens the provider portal. Drives the Free card's "Cancel plan" CTA for a
   * subscriber. The standalone page has no billing view (the former `/billing`
   * route redirects to the dashboard), so there the same CTA opens the portal
   * directly.
   */
  onShowBilling?: () => void
}

/**
 * The pricing body (tier cards, billing-period toggle, rate-card notes) —
 * shared by the standalone {@link Pricing} page and the in-IDE upgrade modal.
 */
export function PricingContent({
  variant = 'page',
  onCheckoutOpened,
  onShowBilling,
}: PricingContentProps) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const { state, refresh } = useAuth<UserProfile>()
  const http = useHttpClient()
  const inModal = variant === 'modal'
  // The subscriber's way out, from the pricing surface itself. In the modal the
  // billing view is one switch away and holds the whole billing picture, so go
  // there; on the page there is no such view, so open the portal itself.
  const { openPortal, pending: portalPending } = useBillingPortal()
  const currentPlanKey =
    (state.user as (UserProfile & { planKey?: string }) | null)?.planKey ?? null
  const [tiers, setTiers] = useState<TierInfo[]>([])
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState(false)
  // Open on the user's OWN billing period, so a yearly subscriber's first
  // paint shows "Current Plan" — not a "Switch to Monthly" CTA they never
  // asked about. (Catalogue keys encode the period: stripeYearly, teamYearly.)
  const [billingPeriod, setBillingPeriod] = useState<'month' | 'year'>(() =>
    currentPlanKey?.includes('Yearly') ? 'year' : 'month',
  )
  const [upgrading, setUpgrading] = useState(false)
  /** Flip the billing period. Every interaction with the toggle does exactly this. */
  const toggleBillingPeriod = useCallback(() => {
    setBillingPeriod((current) => (current === 'month' ? 'year' : 'month'))
  }, [])

  // Whether any card shows a usage stepper for the selected period. The cards
  // that don't then reserve its height, so one card's extra control can't offset
  // its limits table from the others'. Recomputed per period because the packs
  // are configured per price.
  const anyUsageSteppers = tiers.some(
    (tier) => (tier.usageOptions?.[billingPeriod] ?? []).length > 1,
  )

  // Whether any card is per-seat (Team) and so shows a seats-total line. The
  // other cards then reserve that row's height, so Team's total can't offset
  // its limits table from theirs.
  const anySeatTotals = tiers.some((tier) => tier.perSeat && (tier.limits.maxBuilders ?? 1) > 1)

  // The yearly discount ("2 months free") is identical on every paid card, so it
  // shows ONCE beneath the toggle instead of on each card. Read it from the tier
  // data (the first yearly price that carries a savings note) rather than
  // hardcoding — a change in api tiers.ts reprices this line too. Shown only when
  // the Yearly period is selected.
  const yearlySavings =
    tiers
      .map((tier) => tier.prices?.find((p) => p.period === 'year')?.savings)
      .find((savings): savings is string => Boolean(savings)) ?? ''

  // Fetch tiers on mount
  const fetchTiers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/tiers`)
      if (!res.ok) throw new Error(`GET /tiers responded ${res.status}`)
      const json = (await res.json()) as { data: TierInfo[] }
      setTiers(json.data)
      setLoadError(false)
    } catch (error) {
      // Without this the modal rendered a heading over ZERO cards, silently.
      console.error('Failed to fetch pricing tiers from API', error)
      reportHandledFailure('pricing-tiers', error)
      setLoadError(true)
    }
    setLoaded(true)
  }, [])

  useEffect(() => {
    void fetchTiers()
  }, [fetchTiers])

  const handleUpgrade = useCallback(
    async (priceId: string, selectedPlanKey?: string, quantity?: number) => {
      if (!state.user) {
        window.location.href = '/login'
        return
      }
      setUpgrading(true)
      try {
        // The card passes the exact plan key its selection represents (period +
        // usage pack). Fall back to deriving it from the price for older call
        // sites that only pass a priceId.
        const tier = tiers.find((t) => t.prices?.some((p) => p.stripePriceId === priceId))
        const price = tier?.prices?.find((p) => p.stripePriceId === priceId)
        const planKey =
          selectedPlanKey ??
          basePlanKey(tier?.key ?? '', price?.period === 'year' ? 'year' : 'month')
        if (!planKey) {
          // No derivable plan key ('' would request a FREE downgrade) — bail
          // loudly rather than send a plan change the user did not pick.
          reportHandledFailure(
            'plan-upgrade',
            new Error(`No plan key derivable for price ${priceId}`),
          )
          emitNotification('Could not start the upgrade. Please try again.', 'error')
          setUpgrading(false)
          return
        }

        // The plan route is a resource-infrastructure handler: its body is sent
        // FLAT ({ checkoutUrl } on 201, { props } on an in-place 200 update).
        // Only molecule-dev's handwritten routes wrap in a { data } envelope —
        // reading res.data.data here made even a SUCCESSFUL checkout a no-op.
        const res = await http.patch<{ checkoutUrl?: string; props?: unknown }>(
          `/users/${state.user!.id}/plan`,
          // Seats. Sent only when the card actually offered a choice, so a
          // flat-priced plan's request is byte-identical to before.
          quantity && quantity > 1 ? { planKey, quantity } : { planKey },
        )
        const checkoutUrl = res.data?.checkoutUrl
        if (checkoutUrl) {
          // Validate checkout URL points to a trusted payment provider
          let checkoutHost: string | null = null
          try {
            checkoutHost = new URL(checkoutUrl).hostname
          } catch (_error) {
            checkoutHost = null // malformed URL — handled below, never navigated to
          }
          if (checkoutHost === 'stripe.com' || checkoutHost?.endsWith('.stripe.com')) {
            if (inModal) {
              // The IDE tab must never navigate — checkout gets its own tab,
              // and the modal host refreshes the plan on focus-return.
              // `noopener` makes window.open return null even when the tab opens, so the
              // return value cannot be the success signal: open normally and sever the
              // opener by hand (the host was checked above to be Stripe's).
              const win = window.open(checkoutUrl, '_blank')
              if (win) {
                win.opener = null
                onCheckoutOpened?.()
              } else {
                // Popup blocked: window.open ran after an await, outside the
                // user-gesture window. The banner click IS a user gesture, so
                // its link opens fine in a new tab.
                emitNotification('Your browser blocked the checkout window.', 'warning', {
                  label: 'Open checkout',
                  href: checkoutUrl,
                  external: true,
                })
              }
            } else {
              window.location.href = checkoutUrl
            }
          } else {
            // Not a recognized Stripe host (custom checkout domain, malformed
            // URL). Refusing to navigate is right; doing it silently was not.
            reportHandledFailure(
              'plan-upgrade',
              new Error(`Checkout URL host not recognized: ${checkoutHost ?? 'invalid URL'}`),
            )
            emitNotification('Could not open checkout. Please try again.', 'error')
          }
        } else {
          // No checkoutUrl: an existing subscriber's plan was updated in place
          // (200 { props }) — or the response shape changed under us.
          if (res.data && 'props' in res.data) {
            emitNotification('Your plan has been updated.', 'info')
            notifyUsageInvalidated()
            // Re-fetch the profile so currentPlan (and every card's CTA)
            // reflects the new plan immediately — there is no checkout tab
            // whose focus-return would do it.
            void refresh?.()
          } else {
            reportHandledFailure(
              'plan-upgrade',
              new Error(`Unexpected ${res.status} response shape from PATCH /users/:id/plan`),
            )
            emitNotification('Could not start checkout. Please try again.', 'error')
          }
        }
      } catch (error) {
        console.error('Failed to initiate plan upgrade', error)
        reportHandledFailure('plan-upgrade', error)
        // The global interceptor already banners network failures (status 0),
        // 429s, limit 403s, and 5xx — only the remaining 4xx were silent.
        const status = (error as { status?: number }).status ?? 0
        const data = (error as { response?: { data?: Record<string, unknown> } }).response?.data
        const globallyHandled =
          status === 0 ||
          status === 429 ||
          status >= 500 ||
          (status === 403 && typeof data?.limitType === 'string')
        if (!globallyHandled) {
          emitNotification(
            (typeof data?.error === 'string' && data.error) ||
              'Could not start the upgrade. Please try again.',
            'error',
          )
        }
      }
      setUpgrading(false)
    },
    [state.user, http, tiers, inModal, onCheckoutOpened, refresh],
  )

  return (
    <>
      <div className={cm.textCenter} style={{ marginBottom: inModal ? 12 : 40 }}>
        <h1
          className={cm.cn(
            cm.textSize(inModal ? '2xl' : '3xl'),
            cm.fontWeight('bold'),
            cm.sp('mb', inModal ? 1 : 2),
          )}
        >
          Simple, transparent pricing
        </h1>
        <p style={{ opacity: 0.6, maxWidth: 500, margin: '0 auto' }}>
          Start free and scale as you grow.
        </p>

        {/* Billing toggle — ONE control that FLIPS, not two selectable buttons:
            a click anywhere on it switches periods, including a click on the
            half that is already selected. That is what makes it a toggle rather
            than a segmented picker, so it is a real `switch` (one button, one
            aria-checked, Space/Enter and ←/→ all flip it) and the two words are
            decorative labels for the two positions the thumb travels between —
            the accessible name says which state checked means. */}
        <Flex
          align="center"
          justify="center"
          className={cm.cn(cm.sp('mt', inModal ? 6 : 8), cm.sp('mb', 2))}
        >
          {/* mol-bespoke-button: a SWITCH (role="switch"), not a CTA — a pill
              track holding an animated thumb behind two segment labels. Its
              border/background live in `.mol-price-toggle`; only the track's
              own padding + pill radius are inline. */}
          <button
            type="button"
            role="switch"
            aria-checked={billingPeriod === 'year'}
            aria-label={t('pricing.billYearly', undefined, { defaultValue: 'Bill yearly' })}
            data-mol-id="pricing-billing-toggle"
            onClick={toggleBillingPeriod}
            onKeyDown={(event) => {
              if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
              event.preventDefault()
              toggleBillingPeriod()
            }}
            // touchTargetCompact grows the whole switch to 36px on coarse pointers;
            // alignItems keeps the labels vertically centered when it does
            // (a no-op at the desktop height, where items fill the control).
            // mol-price-toggle owns the border + background so the :hover border
            // lift isn't overridden by an inline value.
            className={cm.cn(cm.touchTargetCompact, 'mol-price-toggle')}
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              padding: 3,
              borderRadius: 999,
              cursor: 'pointer',
            }}
          >
            {/* The thumb. Absolutely positioned and translated by whole segment
                widths, so the travel animates instead of one half's fill
                blinking off and the other's on. */}
            <div
              aria-hidden="true"
              data-mol-id="pricing-billing-toggle-thumb"
              style={{
                position: 'absolute',
                top: 3,
                bottom: 3,
                left: 3,
                width: `calc((100% - 6px) / ${BILLING_PERIODS.length})`,
                borderRadius: 999,
                // Solid primary + white label (see billingToggleSegmentStyle):
                // primary-light with black text was hard to read in light theme.
                background: 'var(--color-primary)',
                transform: `translateX(${
                  BILLING_PERIODS.findIndex((period) => period.value === billingPeriod) * 100
                }%)`,
                transition: 'transform 0.2s ease',
              }}
            />
            {BILLING_PERIODS.map((period) => (
              <span
                key={period.value}
                aria-hidden="true"
                data-mol-id={`pricing-billing-${period.value}`}
                className="mol-price-toggle-label"
                data-active={billingPeriod === period.value}
                style={billingToggleSegmentStyle(billingPeriod === period.value)}
              >
                {period.label}
              </span>
            ))}
          </button>
        </Flex>

        {/* The single yearly-savings line, directly beneath the toggle. A
            fixed-height slot so switching Monthly↔Yearly reveals the text without
            shifting the cards below. `--color-success-text` follows the theme
            (success mixed toward the current foreground), so it stays legible in
            both light and dark. */}
        <div
          style={{
            minHeight: 22,
            marginBottom: inModal ? 4 : 8,
            color: 'var(--color-success-text, #4ade80)',
            fontSize: 13,
            fontWeight: 600,
          }}
          data-mol-id="pricing-yearly-savings"
        >
          {billingPeriod === 'year' ? yearlySavings : ''}
        </div>
      </div>

      {!loaded ? (
        <Flex align="center" justify="center" className={cm.sp('py', 12)}>
          <MoleculeSpinner />
        </Flex>
      ) : loadError && tiers.length === 0 ? (
        // The /tiers fetch failed — an explicit retry beats a heading over zero
        // cards (which read as "pricing is broken" with no way forward).
        <Flex
          align="center"
          justify="center"
          direction="column"
          gap="md"
          className={cm.sp('py', 12)}
        >
          <span style={{ opacity: 0.7 }}>Could not load pricing.</span>
          <Button
            variant="outline"
            className={cm.touchTargetCompact}
            data-mol-id="pricing-retry"
            onClick={() => {
              setLoaded(false)
              void fetchTiers()
            }}
          >
            Retry
          </Button>
        </Flex>
      ) : (
        <Flex align="stretch" justify="center" gap="md" style={{ flexWrap: 'wrap' }}>
          {tiers.map((tier) => (
            <TierCard
              key={tier.key}
              tier={tier}
              billingPeriod={billingPeriod}
              currentPlan={currentPlanKey}
              onUpgrade={handleUpgrade}
              loading={upgrading}
              upgradeHref={inModal && !state.user ? '/signup' : undefined}
              onCancelPlan={
                tier.key === 'free' && currentPlanKey && !isGrantedSeatPlan(currentPlanKey)
                  ? inModal
                    ? onShowBilling
                    : () => void openPortal()
                  : undefined
              }
              cancelPending={portalPending}
              reserveUsageRow={anyUsageSteppers}
              reserveSeatTotal={anySeatTotals}
            />
          ))}
        </Flex>
      )}

      <div className={cm.cn(cm.textCenter, cm.sp('mt', 8), !inModal && cm.sp('mb', 8))}>
        {/* The whole pill is the mailto link (not just "Contact us"), and it
            carries the same border + hover-to-primary lift as the toggle/steppers
            (mol-price-stepper). Text colors are unchanged: "Need Enterprise?"
            inherits, "Contact us" keeps primary-light. */}
        <a
          href="mailto:enterprise@molecule.dev"
          className="mol-price-stepper"
          style={{
            display: 'inline-block',
            borderRadius: 999,
            padding: '8px 20px',
            fontSize: 14,
            color: 'inherit',
            textDecoration: 'none',
            cursor: 'pointer',
          }}
        >
          Need Enterprise?{' '}
          <span style={{ color: 'var(--color-primary-light)', fontWeight: 600 }}>Contact us</span>
        </a>
      </div>
    </>
  )
}

/**
 * The standalone `/pricing` page — the site Header plus {@link PricingContent}.
 * The in-IDE upgrade modal renders the same content without navigating the
 * workspace tab.
 */
export function Pricing() {
  const cm = getClassMap()
  return (
    <>
      <Header />
      <div
        className={cm.cn('mol-page-container', cm.sp('pt', 16))}
        // Vertical styles inline LONGHAND — a padding/margin SHORTHAND would
        // override .mol-page-container's inline padding and cm.sp('pt', 16)
        // (inline beats class), leaving content flush under the fixed header.
        // marginTop adds breathing room below it.
        style={{
          marginTop: 24,
          paddingBottom: 48,
        }}
      >
        <PricingContent />
      </div>
    </>
  )
}
